import { useEffect, useState, useCallback } from "react";
import { Search, Users, Package } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate, fmtDateTime } from "../../lib/api";
import { PageHeader, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function StoreCustomers() {
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState("");
  const [ordersFor, setOrdersFor] = useState(null);
  const [orders, setOrders] = useState(null);

  const viewOrders = async (c) => {
    setOrdersFor(c);
    setOrders(null);
    try { const { data } = await api.get(`/store-admin/customers/${c.id}/orders`); setOrders(data.orders); }
    catch { toast.error("Could not load orders"); }
  };

  const load = useCallback(() => { api.get("/store-admin/customers", { params: { q } }).then((r) => setCustomers(r.data)); }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="store-customers">
      <PageHeader title="Customers" subtitle="Everyone who has ordered from your store." />
      <div className="relative max-w-sm mb-6">
        <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="pl-10 h-11 rounded-full" />
      </div>
      {!customers ? <LoadingRows /> : customers.length === 0 ? (
        <EmptyState icon={Users} title="No customers yet" body="Customers are added automatically when they place an order." />
      ) : (
        <TableShell testid="customers-table" head={["Customer", "Contact", "Orders", "Total Spent", "Since", ""]}>
          {customers.map((c) => (
            <TR key={c.id} testid={`customer-row-${c.id}`}>
              <TD>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center font-display font-bold text-sm">{c.name?.[0]?.toUpperCase()}</div>
                  <span className="font-bold">{c.name}</span>
                </div>
              </TD>
              <TD><p className="text-sm">{c.email || "—"}</p><p className="text-xs text-muted-foreground">{c.phone}</p></TD>
              <TD className="font-bold">{c.total_orders}</TD>
              <TD className="font-bold">{fmtINR(c.total_spent)}</TD>
              <TD className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</TD>
              <TD>
                <button data-testid={`customer-orders-${c.id}`} onClick={() => viewOrders(c)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-border hover:bg-muted transition-colors">
                  <Package className="h-3.5 w-3.5" /> Orders
                </button>
              </TD>
            </TR>
          ))}
        </TableShell>
      )}

      <Dialog open={!!ordersFor} onOpenChange={() => setOrdersFor(null)}>
        <DialogContent data-testid="customer-orders-dialog" className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Orders — {ordersFor?.name}</DialogTitle></DialogHeader>
          {!orders ? <LoadingRows rows={3} /> : orders.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No orders from this customer yet.</p>
          ) : (
            <div className="space-y-2.5">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center gap-3 border border-border rounded-md p-3.5" data-testid={`cust-order-${o.order_number}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold">{o.order_number}</p>
                    <p className="text-xs text-muted-foreground">{fmtDateTime(o.created_at)} · {o.items.reduce((n, i) => n + i.qty, 0)} item(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-extrabold">{fmtINR(o.total)}</p>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{o.status} · {o.payment_status}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
